import type { ShaderProgram } from './ShaderProgram';

export interface InstanceAttrib {
  name: string;
  size: number;    // components (e.g. 2 for vec2, 3 for vec3)
  divisor: number; // 1 = per-instance, 0 = per-vertex
}

/**
 * VAO-based instanced mesh. Separates geometry (shared) from
 * per-instance data (positions, colors, etc.).
 *
 * Uses WebGL 2.0 native instancing — no extensions needed.
 */
export class InstancedMesh {
  readonly vao: WebGLVertexArrayObject;
  private _gl: WebGL2RenderingContext;
  private _geometryVbo: WebGLBuffer;
  private _instanceVbo: WebGLBuffer;
  private _ibo: WebGLBuffer | null = null;
  private _vertexCount: number;
  private _indexCount = 0;
  private _instanceCount = 0;

  constructor(
    gl: WebGL2RenderingContext,
    shader: ShaderProgram,
    geometry: Float32Array,
    geometryAttribs: InstanceAttrib[],
    instanceAttribs: InstanceAttrib[],
    indices?: Uint16Array,
  ) {
    this._gl = gl;

    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);
    this.vao = vao;

    // --- Geometry buffer (shared across all instances) ---
    const geoStride = geometryAttribs.reduce((s, a) => s + a.size, 0);
    this._vertexCount = geometry.length / geoStride;

    this._geometryVbo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this._geometryVbo);
    gl.bufferData(gl.ARRAY_BUFFER, geometry, gl.STATIC_DRAW);

    let offset = 0;
    for (const attr of geometryAttribs) {
      const loc = shader.getAttrib(attr.name);
      if (loc >= 0) {
        gl.enableVertexAttribArray(loc);
        gl.vertexAttribPointer(loc, attr.size, gl.FLOAT, false, geoStride * 4, offset * 4);
        gl.vertexAttribDivisor(loc, 0);
      }
      offset += attr.size;
    }

    // --- Instance buffer (unique per hex) ---
    this._instanceVbo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this._instanceVbo);
    gl.bufferData(gl.ARRAY_BUFFER, 0, gl.DYNAMIC_DRAW);

    const instStride = instanceAttribs.reduce((s, a) => s + a.size, 0);
    offset = 0;
    for (const attr of instanceAttribs) {
      const loc = shader.getAttrib(attr.name);
      if (loc >= 0) {
        gl.enableVertexAttribArray(loc);
        gl.vertexAttribPointer(loc, attr.size, gl.FLOAT, false, instStride * 4, offset * 4);
        gl.vertexAttribDivisor(loc, 1);
      }
      offset += attr.size;
    }

    // --- Index buffer (optional) ---
    if (indices) {
      this._ibo = gl.createBuffer()!;
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this._ibo);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);
      this._indexCount = indices.length;
    }

    gl.bindVertexArray(null);
  }

  /** Upload new instance data. Call when visible hexes change. */
  updateInstances(data: Float32Array, count: number): void {
    this._instanceCount = count;
    const gl = this._gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this._instanceVbo);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.DYNAMIC_DRAW);
  }

  draw(): void {
    if (this._instanceCount === 0) return;
    const gl = this._gl;
    gl.bindVertexArray(this.vao);

    if (this._ibo) {
      gl.drawElementsInstanced(gl.TRIANGLES, this._indexCount, gl.UNSIGNED_SHORT, 0, this._instanceCount);
    } else {
      gl.drawArraysInstanced(gl.TRIANGLES, 0, this._vertexCount, this._instanceCount);
    }

    gl.bindVertexArray(null);
  }

  dispose(): void {
    const gl = this._gl;
    gl.deleteVertexArray(this.vao);
    gl.deleteBuffer(this._geometryVbo);
    gl.deleteBuffer(this._instanceVbo);
    if (this._ibo) gl.deleteBuffer(this._ibo);
  }
}
